import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ExternalLink, Loader2, Upload, X } from 'lucide-react'
import { Textarea } from 'shared/ui/textarea'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Checkbox } from 'shared/ui/checkbox'
import { Button } from 'shared/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { RadioGroup, RadioGroupItem } from 'shared/ui/radio-group'
import { cn } from 'shared/ui/utils'
import { buildAttachmentUrl } from 'shared/utils/attachmentUrl'
import type { TemplateField, TemplateFieldDatasourceItem } from 'shared/api/templateService'
import { getFieldDatasourceOptions } from 'shared/api/templateService'
import { uploadPermitAttachment } from 'modules/permit/api/permitService'
import { SearchMultiSelectField, SearchSelectField } from '../../SearchSelectField'
import type { ParsedFieldConfig, ResolvedInformationMessage } from '../utils/templateFieldUtils'
import {
  isSearchSelectMultiple,
  resolveFieldInformationMessages,
  tryParseJson,
} from '../utils/templateFieldUtils'

interface AttachmentValue {
  fileName: string
  storedFileName?: string
  storagePath: string
  contentType?: string
  size?: number
}

const buildAttachmentFileUrl = (path?: string | null): string | null => buildAttachmentUrl(path)

const parseAttachmentValue = (rawValue: string): AttachmentValue | null => {
  const trimmed = rawValue.trim()
  if (!trimmed) {
    return null
  }

  const parsed = tryParseJson<Record<string, unknown>>(trimmed)
  if (parsed && typeof parsed === 'object') {
    const storagePathRaw = parsed.storagePath
    const fileNameRaw = parsed.fileName
    if (typeof storagePathRaw === 'string' && storagePathRaw.trim()) {
      const fallbackName = storagePathRaw.split('/').pop()?.trim() || storagePathRaw
      return {
        fileName: typeof fileNameRaw === 'string' && fileNameRaw.trim() ? fileNameRaw.trim() : fallbackName,
        storedFileName:
          typeof parsed.storedFileName === 'string' && parsed.storedFileName.trim()
            ? parsed.storedFileName.trim()
            : undefined,
        storagePath: storagePathRaw.trim(),
        contentType:
          typeof parsed.contentType === 'string' && parsed.contentType.trim()
            ? parsed.contentType.trim()
            : undefined,
        size: typeof parsed.size === 'number' ? parsed.size : undefined,
      }
    }
  }

  if (trimmed.startsWith('/')) {
    return {
      fileName: trimmed.split('/').pop() ?? trimmed,
      storagePath: trimmed,
    }
  }

  return null
}

interface FieldDependencyInfo {
  params: Record<string, string>
  dependencyMissing: boolean
  config?: ParsedFieldConfig | null
}

interface TemplateFieldRendererOptions {
  getFieldOptions: (fieldId: string) => TemplateFieldDatasourceItem[]
  getTemplateFieldDisplayValue: (field: TemplateField) => string
  getStoredTemplateFieldLabel: (field: TemplateField) => string | undefined
  getFieldValueByCode: (fieldCode: string) => string | undefined
  isFieldLoading: (fieldId: string) => boolean
  setTemplateFieldValue: (field: TemplateField, value: string | boolean | null) => void
  setTemplateFieldLabel: (field: TemplateField, label: string | null) => void
  tenantId: string
  permitNumber?: string | null
  parseFieldConfig: (field: TemplateField) => ParsedFieldConfig | null
  searchSelectLabels: Record<string, string>
  setSearchSelectLabels: React.Dispatch<React.SetStateAction<Record<string, string>>>
  isReadOnly: boolean
  canApproveCurrentStage: boolean
  getFieldDependencyInfo: (field: TemplateField) => FieldDependencyInfo
}

interface FieldRenderOptions {
  disabled?: boolean
}

interface InformationMessageOptionCandidate {
  value: string
  label: string
  code?: string
}

const normalizeLooseInformationKey = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s_-]+/g, '')

const resolveOptionCode = (
  option: Partial<TemplateFieldDatasourceItem> & {
    code?: string
    columns?: Record<string, string | null>
  },
  preferredColumn = 'code',
): string | undefined => {
  const directCode =
    typeof option.code === 'string' && option.code.trim().length > 0
      ? option.code.trim()
      : undefined
  if (directCode) {
    return directCode
  }

  const normalizedPreferredColumn = normalizeLooseInformationKey(preferredColumn)
  for (const [key, rawValue] of Object.entries(option.columns ?? {})) {
    if (normalizeLooseInformationKey(key) !== normalizedPreferredColumn) {
      continue
    }
    if (typeof rawValue === 'string' && rawValue.trim().length > 0) {
      return rawValue.trim()
    }
  }

  return undefined
}

const extractInformationMessageCandidates = (values: string[]): string[] => {
  const candidates = new Set<string>()

  values.forEach((value) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return
    }

    candidates.add(trimmed)

    const parentheticalMatches = Array.from(trimmed.matchAll(/\(([^)]+)\)/g))
    parentheticalMatches.forEach((match) => {
      const inner = match[1]?.trim()
      if (inner) {
        candidates.add(inner)
      }
    })

    const withoutParentheses = trimmed.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim()
    if (withoutParentheses) {
      candidates.add(withoutParentheses)
    }
  })

  return Array.from(candidates)
}

function FieldInformationMessageList({ items }: { items: ResolvedInformationMessage[] }) {
  if (items.length === 0) {
    return null
  }

  const variantClassNameMap: Record<ResolvedInformationMessage['variant'], string> = {
    info: 'border-sky-200 bg-sky-50 text-sky-800',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
    danger: 'border-rose-200 bg-rose-50 text-rose-700',
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={item.key}
          className={cn(
            'rounded-md border px-3 py-2 text-sm',
            variantClassNameMap[item.variant] ?? variantClassNameMap.info,
          )}
        >
          {item.title ? <p className="mb-1 font-medium">{item.title}</p> : null}
          {item.messages.length === 1 ? (
            <p className="leading-6">{item.messages[0]}</p>
          ) : (
            <div className="space-y-1">
              {item.messages.map((message, index) => (
                <p key={`${item.key}-${index}`} className="leading-6">
                  {message}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export function createTemplateFieldRenderer({
  getFieldOptions,
  getTemplateFieldDisplayValue,
  getStoredTemplateFieldLabel,
  getFieldValueByCode,
  isFieldLoading,
  setTemplateFieldValue,
  setTemplateFieldLabel,
  tenantId,
  permitNumber,
  parseFieldConfig,
  searchSelectLabels,
  setSearchSelectLabels,
  isReadOnly,
  canApproveCurrentStage,
  getFieldDependencyInfo,
}: TemplateFieldRendererOptions) {
  const resolveVisibleFlag = (field: TemplateField): boolean | undefined => {
    const direct = (field as any)?.visible
    if (typeof direct === 'boolean') {
      return direct
    }
    const parsed = tryParseJson<Record<string, unknown>>(field.config)
    if (parsed && typeof parsed === 'object') {
      for (const [key, value] of Object.entries(parsed)) {
        const normalizedKey = key.trim().toLowerCase()
        if (normalizedKey === 'visible' || normalizedKey === 'isvisible') {
          if (typeof value === 'boolean') {
            return value
          }
          if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase()
            if (normalized === 'true') return true
            if (normalized === 'false') return false
          }
        }
      }
    }
    return undefined
  }

  const resolveVisibilityRule = (field: TemplateField): Record<string, unknown> | null => {
    const normalizeCandidate = (candidate: unknown): Record<string, unknown> | null => {
      if (!candidate || typeof candidate !== 'object') {
        return null
      }
      const obj = candidate as Record<string, unknown>
      const nestedCandidates: Array<unknown> = [obj.visibleWhen, obj.visibilityWhen, obj.showWhen]
      for (const nested of nestedCandidates) {
        if (nested && typeof nested === 'object') {
          return nested as Record<string, unknown>
        }
      }
      return obj
    }

    const fromColumn = normalizeCandidate(tryParseJson<Record<string, unknown>>(field.visibilityRules))
    if (fromColumn) {
      return fromColumn
    }

    const fromConfig = normalizeCandidate(tryParseJson<Record<string, unknown>>(field.config))
    if (fromConfig) {
      const nestedCandidates: Array<unknown> = [
        (fromConfig as any).visibleWhen,
        (fromConfig as any).visibilityWhen,
        (fromConfig as any).showWhen,
      ]
      for (const nested of nestedCandidates) {
        if (nested && typeof nested === 'object') {
          return nested as Record<string, unknown>
        }
      }
    }

    return null
  }

  const resolveDatasourceStaticParams = (field: TemplateField): Record<string, string> => {
    const raw = field.datasource?.paramsJson
    if (!raw) {
      return {}
    }

    const parsed = tryParseJson<Record<string, unknown>>(raw)
    if (!parsed || typeof parsed !== 'object') {
      return {}
    }

    const replaceTokens = (value: string) =>
      value
        .replace(/@tenantId/gi, tenantId)
        .replace(/@tenant_id/gi, tenantId)
        .replace(/@tenant/gi, tenantId)

    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
      if (typeof value !== 'string') {
        return acc
      }
      const trimmed = value.trim()
      if (!trimmed) {
        return acc
      }
      acc[key] = replaceTokens(trimmed)
      return acc
    }, {})
  }

  const buildDatasourceParams = (
    field: TemplateField,
    dependencyParams: Record<string, string> = {},
  ): Record<string, string> => {
    const staticParams = resolveDatasourceStaticParams(field)
    const mergedParams = {
      ...staticParams,
      tenant: tenantId,
      tenantId,
      tenant_id: tenantId,
      ...dependencyParams,
    }

    return Object.entries(mergedParams).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        acc[key] = String(value)
      }
      return acc
    }, {})
  }

  const isVisibilityRuleSatisfied = (rule: Record<string, unknown>): boolean => {
    const rawCode =
      rule.fieldCode ?? rule.dependsOn ?? rule.field ?? rule.code
    const fieldCode = typeof rawCode === 'string' ? rawCode.trim() : ''
    if (!fieldCode) {
      return true
    }

    const actualValue = (getFieldValueByCode(fieldCode) ?? '').trim()

    if (typeof rule.exists === 'boolean') {
      return rule.exists ? actualValue.length > 0 : actualValue.length === 0
    }

    const expected = (rule.equals as unknown) ?? (rule.value as unknown)
    if (expected === undefined) {
      return true
    }

    if (Array.isArray(expected)) {
      return expected.some((item) => String(item ?? '').trim() === actualValue)
    }

    if (Array.isArray(rule.in)) {
      return (rule.in as unknown[]).some((item) => String(item ?? '').trim() === actualValue)
    }

    return String(expected ?? '').trim() === actualValue
  }

  return function renderTemplateFieldInput(field: TemplateField, renderOptions?: FieldRenderOptions) {
    const fieldType = (field.fieldType ?? '').trim().toLowerCase()
    const code = field.code ?? field.fieldId
    const value = getTemplateFieldDisplayValue(field)
    const label = field.label?.trim() || code || 'Additional Field'
    const isRequired = field.required
    const parsedFieldConfig = parseFieldConfig(field)
    const datasourceUiConfig = tryParseJson<Record<string, unknown> | null>(
      field.datasource?.uiConfigJson,
    )
    const controlHints: Array<string | undefined> = [
      parsedFieldConfig?.controlType,
      parsedFieldConfig?.renderAs,
      parsedFieldConfig?.inputType,
    ]
    const remoteSearchOnlyRaw =
      datasourceUiConfig && typeof datasourceUiConfig === "object"
        ? (datasourceUiConfig as Record<string, unknown>).remoteSearchOnly
        : null
    const remoteSearchOnly =
      remoteSearchOnlyRaw === true || String(remoteSearchOnlyRaw ?? "").trim().toLowerCase() === "true"
    const remoteSearchMinCharsRaw =
      datasourceUiConfig && typeof datasourceUiConfig === "object"
        ? Number((datasourceUiConfig as Record<string, unknown>).minSearchLength)
        : Number.NaN
    const remoteSearchMinChars =
      Number.isFinite(remoteSearchMinCharsRaw) && remoteSearchMinCharsRaw > 0
        ? Math.min(10, Math.floor(remoteSearchMinCharsRaw))
        : 2

    if (datasourceUiConfig && typeof datasourceUiConfig === 'object') {
      const rawControlType = (datasourceUiConfig as Record<string, unknown>).controlType
      const rawRenderAs = (datasourceUiConfig as Record<string, unknown>).renderAs
      const rawInputType = (datasourceUiConfig as Record<string, unknown>).inputType
      const rawVariant = (datasourceUiConfig as Record<string, unknown>).variant

      if (typeof rawControlType === 'string') {
        controlHints.push(rawControlType)
      }
      if (typeof rawRenderAs === 'string') {
        controlHints.push(rawRenderAs)
      }
      if (typeof rawInputType === 'string') {
        controlHints.push(rawInputType)
      }
      if (typeof rawVariant === 'string') {
        controlHints.push(rawVariant)
      }
    }

    const renderLabel = () => (
      <Label className="text-sm text-gray-600">
        {label}
        {isRequired ? <span className="text-red-500">*</span> : null}
      </Label>
    )
    const renderInformationMessages = (selectedValues: string[]) => (
      <FieldInformationMessageList
        items={resolveFieldInformationMessages(
          parsedFieldConfig,
          extractInformationMessageCandidates(selectedValues),
        )}
      />
    )
    // An explicit renderOptions.disabled is authoritative (callers like WorkflowSectionsSection
    // and GeneralDetailsSection already fold isReadOnly/canApproveCurrentStage plus any
    // role-based bypass - e.g. ENQA's always-editable exception - into that computed value).
    // Only fall back to the generic isReadOnly-without-approve-rights default when the caller
    // didn't specify one; previously this OR'd the fallback in regardless, so an explicit
    // `disabled: false` from a bypass could never actually re-enable the control.
    const isControlDisabled = renderOptions?.disabled ?? (isReadOnly && !canApproveCurrentStage)

    const staticOptions: InformationMessageOptionCandidate[] =
      field.options?.map((option) => ({
        value: option.value ?? option.label ?? option.optionId ?? '',
        label: option.label ?? option.value ?? option.optionId ?? '',
        code: option.value ?? option.label ?? option.optionId ?? '',
      })) ?? []

    const dynamicOptions = getFieldOptions(field.fieldId)
    const datasourcePresetOptions: InformationMessageOptionCandidate[] =
      dynamicOptions.length > 0
        ? dynamicOptions.map((item) => ({
            value: item.value,
            label: item.label ?? item.value,
            code: resolveOptionCode(item),
          }))
        : field.datasource?.items?.map((item) => ({
              value: item.value,
              label: item.label ?? item.value,
              code: resolveOptionCode(item),
            })) ?? []

    const combinedOptionsRaw =
      datasourcePresetOptions.length > 0
        ? datasourcePresetOptions.map((option) => ({
            value: option.value ?? option.label,
            label: option.label ?? option.value ?? option.value,
            code: option.code,
          }))
        : staticOptions

    // Dedupe by value as the final gate before rendering: duplicates can come
    // from field.datasource.items (a static preset baked into the template
    // config, used while the live datasource fetch is still in flight) or
    // field.options, not just the fetched dynamicOptions path - any of these
    // rendering the same value twice breaks React's key uniqueness for
    // SearchableSelectDropdown's <SelectItem key={`${fieldId}_${value}`}>.
    const seenOptionValues = new Set<string>()
    const combinedOptions = combinedOptionsRaw.filter((option) => {
      const key = (option.value ?? '').trim().toLowerCase()
      if (!key || seenOptionValues.has(key)) {
        return false
      }
      seenOptionValues.add(key)
      return true
    })

    const dependencyInfo = getFieldDependencyInfo(field)

    const directVisibleFlag = resolveVisibleFlag(field)
    if (directVisibleFlag === false) {
      return null
    }

    const visibilityRule = resolveVisibilityRule(field)
    if (visibilityRule && !isVisibilityRuleSatisfied(visibilityRule)) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[permit][visibility] hidden', {
          fieldId: field.fieldId,
          code: field.code,
          rule: visibilityRule,
          dependsValue: (() => {
            const rawCode =
              (visibilityRule.fieldCode as unknown) ??
              (visibilityRule.dependsOn as unknown) ??
              (visibilityRule.field as unknown) ??
              (visibilityRule.code as unknown)
            const code = typeof rawCode === 'string' ? rawCode.trim() : ''
            return code ? getFieldValueByCode(code) : undefined
          })(),
        })
      }
      return null
    }

    switch (fieldType) {
      case 'computed': {
        const visible = resolveVisibleFlag(field)
        // Default: hide computed fields unless explicitly marked visible
        if (visible === false || visible === undefined) {
          return null
        }
        const loading = isFieldLoading(field.fieldId)
        const displayValue = value || '—'
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <div
              className={`rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm ${
                loading ? 'text-gray-400 italic' : 'text-gray-700'
              }`}
            >
              {loading ? 'กำลังคำนวณ...' : displayValue}
            </div>
          </div>
        )
      }
      case 'textarea':
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <Textarea
              value={value}
              onChange={(event) => setTemplateFieldValue(field, event.target.value)}
              className="bg-gray-50 border-gray-200"
              disabled={isControlDisabled}
            />
          </div>
        )
      case 'attachment':
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <AttachmentFieldInput
              fieldCode={field.code}
              fieldId={field.fieldId}
              tenantId={tenantId}
              permitNumber={permitNumber}
              value={value}
              disabled={isControlDisabled}
              onChange={(nextValue) => setTemplateFieldValue(field, nextValue)}
            />
          </div>
        )
      case 'select': {
        const currentValue = value || '_empty'
        const loading = isFieldLoading(field.fieldId)
        const datasourceParams = buildDatasourceParams(field, dependencyInfo?.params ?? {})
        const datasourceParamsKey = JSON.stringify(datasourceParams)
        const selectedValues =
          currentValue && currentValue !== '_empty' ? [currentValue] : []
        const selectedLabels =
          currentValue && currentValue !== '_empty'
            ? combinedOptions
                .filter((option) => (option.value ?? '').trim() === currentValue.trim())
                .map((option) => option.label ?? option.value ?? '')
            : []
        const selectedCodes =
          currentValue && currentValue !== '_empty'
            ? combinedOptions
                .filter((option) => (option.value ?? '').trim() === currentValue.trim())
                .map((option) => option.code ?? '')
                .filter((optionCode) => optionCode.trim().length > 0)
            : []
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <SearchableSelectDropdown
              fieldId={field.fieldId}
              value={currentValue}
              placeholder={`Select ${label}`}
              options={combinedOptions}
              isLoading={loading}
              disabled={
                isControlDisabled ||
                (combinedOptions.length === 0 && !field.datasource)
              }
              datasourceParams={datasourceParams}
              datasourceParamsKey={datasourceParamsKey}
              selectedValues={selectedValues}
              canSearchRemotely={Boolean(field.datasource)}
              remoteSearchOnly={remoteSearchOnly}
              remoteSearchMinChars={remoteSearchMinChars}
              onChange={(selected) =>
                setTemplateFieldValue(field, selected === '_empty' ? '' : selected)
              }
            />
            {loading && currentValue === '_empty' && combinedOptions.length === 0 ? (
              <p className="text-xs text-gray-500">Loading options...</p>
            ) : null}
            {renderInformationMessages([...selectedValues, ...selectedLabels, ...selectedCodes])}
          </div>
        )
      }
      case 'multiselect': {
        const selectedValues = value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
        const selectedLabels = selectedValues.flatMap((selectedValue) =>
          combinedOptions
            .filter((option) => (option.value ?? '').trim() === selectedValue.trim())
            .map((option) => option.label ?? option.value ?? ''),
        )
        const selectedCodes = selectedValues.flatMap((selectedValue) =>
          combinedOptions
            .filter((option) => (option.value ?? '').trim() === selectedValue.trim())
            .map((option) => option.code ?? '')
            .filter((optionCode) => optionCode.trim().length > 0),
        )
        const datasourceParams = buildDatasourceParams(field, dependencyInfo?.params ?? {})
        const datasourceParamsKey = JSON.stringify(datasourceParams)

        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <MultiSelectDropdown
              fieldId={field.fieldId}
              placeholder={`Select ${label}`}
              options={combinedOptions}
              selectedValues={selectedValues}
              isLoading={isFieldLoading(field.fieldId)}
              disabled={isControlDisabled}
              datasourceParams={datasourceParams}
              datasourceParamsKey={datasourceParamsKey}
              canSearchRemotely={Boolean(field.datasource)}
              remoteSearchOnly={remoteSearchOnly}
              remoteSearchMinChars={remoteSearchMinChars}
              onChange={(nextValues) => setTemplateFieldValue(field, nextValues.join(','))}
            />
            {renderInformationMessages([...selectedValues, ...selectedLabels, ...selectedCodes])}
          </div>
        )
      }
      case 'checkbox':
        return (
          <div key={code} className="flex items-center space-x-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <Checkbox
              id={code}
              checked={value === 'true'}
              onCheckedChange={(state) => setTemplateFieldValue(field, state === true)}
              className="border-gray-300"
              disabled={isControlDisabled}
            />
            <Label htmlFor={code} className="text-sm text-gray-600">
              {label}
              {isRequired ? <span className="text-red-500">*</span> : null}
            </Label>
          </div>
        )
      case 'number':
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <Input
              type="number"
              value={value}
              onChange={(event) => setTemplateFieldValue(field, event.target.value)}
              className="bg-gray-50 border-gray-200"
              disabled={isControlDisabled}
            />
          </div>
        )
      case 'radio':
      case 'radio_button':
        const radioSelectedLabels = value
          ? combinedOptions
              .filter((option) => (option.value ?? '').trim() === value.trim())
              .map((option) => option.label ?? option.value ?? '')
          : []
        const radioSelectedCodes = value
          ? combinedOptions
              .filter((option) => (option.value ?? '').trim() === value.trim())
              .map((option) => option.code ?? '')
              .filter((optionCode) => optionCode.trim().length > 0)
          : []
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            {combinedOptions.length === 0 ? (
              <p className="text-sm text-gray-400 italic">ยังไม่มีตัวเลือกให้เลือก</p>
            ) : (
              <RadioGroup
                value={value}
                onValueChange={(selected) => setTemplateFieldValue(field, selected)}
                className="space-y-2"
                disabled={isControlDisabled}
              >
                {combinedOptions.map((option) => (
                  <div key={`${code}_${option.value}`} className="flex items-center gap-2">
                    <RadioGroupItem value={option.value ?? ''} id={`${code}_${option.value}`} />
                    <Label htmlFor={`${code}_${option.value}`} className="text-sm text-gray-600">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
            {renderInformationMessages(value ? [value, ...radioSelectedLabels, ...radioSelectedCodes] : [])}
          </div>
        )
      case 'search_select': {
        const dependencyLabel = dependencyInfo?.config?.dependsOn
        const isMultiple = isSearchSelectMultiple(field)
        const normalizeValue = (input?: string | null) => (input ?? '').trim().toLowerCase()
        const isGuidLike = (input?: string | null) =>
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            (input ?? '').trim(),
          )
        const storedValue = value?.trim() ?? ''
        const storedLabel = getStoredTemplateFieldLabel(field)?.trim() ?? ''

        if (isMultiple) {
          const parseMultiLabelMap = (raw: string): Record<string, string> => {
            const trimmed = raw.trim()
            if (!trimmed) {
              return {}
            }
            const parsed = tryParseJson<Record<string, unknown>>(trimmed)
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
              return {}
            }
            return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, label]) => {
              if (typeof label === 'string' && label.trim().length > 0) {
                acc[normalizeValue(key)] = label.trim()
              }
              return acc
            }, {})
          }

          const storedValues = storedValue
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
          const cachedLabelSource = (searchSelectLabels[field.fieldId] ?? storedLabel).trim()
          const cachedLabelMap = parseMultiLabelMap(cachedLabelSource)
          const cachedLabels = cachedLabelSource
            .split(',')
            .map((item) => item.trim())
          const selections = storedValues.map((storedItem, index) => {
            const normalizedStoredValue = normalizeValue(storedItem)
            const optionMatch =
              getFieldOptions(field.fieldId).find(
                (option) => normalizeValue(option.value) === normalizedStoredValue,
              ) ??
              field.datasource?.items?.find(
                (item) => normalizeValue(item.value) === normalizedStoredValue,
              )
            const cachedLabel = cachedLabelMap[normalizedStoredValue] ?? cachedLabels[index]
            const shouldPreferOptionLabel =
              Boolean(optionMatch?.label) &&
              (!cachedLabel || cachedLabel === storedItem || isGuidLike(cachedLabel))
            const derivedLabel =
              (shouldPreferOptionLabel ? optionMatch?.label : cachedLabel) ??
              optionMatch?.label ??
              storedItem

            return {
              value: storedItem,
              label: derivedLabel || storedItem,
              code: resolveOptionCode(optionMatch ?? {}),
            }
          })

          return (
            <div key={code} className="space-y-2">
              <SearchMultiSelectField
                field={field}
                value={selections}
                onChange={(selectionValues) => {
                  const nextValue = selectionValues.map((item) => item.value).join(',')
                  const nextLabel = selectionValues.map((item) => item.label).join(', ')
                  const nextLabelMap = selectionValues.reduce<Record<string, string>>((acc, item) => {
                    const key = normalizeValue(item.value)
                    if (key) {
                      acc[key] = item.label
                    }
                    return acc
                  }, {})
                  setTemplateFieldValue(field, nextValue)
                  setTemplateFieldLabel(field, nextLabel || null)
                  setSearchSelectLabels((prev) => ({
                    ...prev,
                    [field.fieldId]: JSON.stringify(nextLabelMap),
                  }))
                }}
                tenantId={tenantId}
                dependencyParams={dependencyInfo?.params ?? {}}
                dependencyMissing={dependencyInfo?.dependencyMissing ?? false}
                dependencyLabel={dependencyLabel}
                disabled={isControlDisabled}
              />
              {renderInformationMessages([
                ...storedValues,
                ...selections.map((item) => item.label),
                ...selections.map((item) => item.code ?? '').filter((item) => item.trim().length > 0),
              ])}
            </div>
          )
        }

        const normalizedStoredValue = normalizeValue(storedValue)
        const optionMatch =
          getFieldOptions(field.fieldId).find(
            (option) => normalizeValue(option.value) === normalizedStoredValue,
          ) ??
          field.datasource?.items?.find(
            (item) => normalizeValue(item.value) === normalizedStoredValue,
          )
        const cachedLabel = (searchSelectLabels[field.fieldId] ?? storedLabel).trim()
        const shouldPreferOptionLabel =
          Boolean(optionMatch?.label) &&
          (!cachedLabel || cachedLabel === storedValue || isGuidLike(cachedLabel))
        const derivedLabel =
          (shouldPreferOptionLabel ? optionMatch?.label : cachedLabel) ??
          optionMatch?.label ??
          (storedValue ? storedValue : '')
        const selection =
          storedValue.length > 0
            ? {
                value: storedValue,
                label: derivedLabel || storedValue,
                code: resolveOptionCode(optionMatch ?? {}),
              }
            : null

        return (
          <div key={code} className="space-y-2">
            <SearchSelectField
              field={field}
              value={selection}
              onChange={(selectionValue) => {
                const nextValue = selectionValue?.value ?? ''
                setTemplateFieldValue(field, nextValue)
                setTemplateFieldLabel(field, selectionValue?.label ?? null)
                setSearchSelectLabels((prev) => ({
                  ...prev,
                  [field.fieldId]: selectionValue?.label ?? '',
                }))
              }}
              tenantId={tenantId}
              dependencyParams={dependencyInfo?.params ?? {}}
              dependencyMissing={dependencyInfo?.dependencyMissing ?? false}
              dependencyLabel={dependencyLabel}
              disabled={isControlDisabled}
            />
            {renderInformationMessages(
              storedValue
                ? [
                    storedValue,
                    derivedLabel || storedValue,
                    ...(selection?.code ? [selection.code] : []),
                  ]
                : [],
            )}
          </div>
        )
      }
      default:
        return (
          <div key={code} className="space-y-2">
            {renderLabel()}
            <Input
              value={value}
              onChange={(event) => setTemplateFieldValue(field, event.target.value)}
              className="bg-gray-50 border-gray-200"
              disabled={isControlDisabled}
            />
          </div>
        )
    }
  }
}

interface AttachmentFieldInputProps {
  fieldId: string
  fieldCode?: string | null
  tenantId: string
  permitNumber?: string | null
  value: string
  disabled: boolean
  onChange: (value: string) => void
}

function AttachmentFieldInput({
  fieldId,
  fieldCode,
  tenantId,
  permitNumber,
  value,
  disabled,
  onChange,
}: AttachmentFieldInputProps) {
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [draftPermitKey] = useState(() => {
    const tenantSeed = tenantId.replace(/-/g, '').slice(0, 8) || 'tenant'
    const randomSeed = Math.random().toString(36).slice(2, 8)
    return `DRAFT-${tenantSeed}-${Date.now()}-${randomSeed}`
  })

  const attachment = useMemo(() => parseAttachmentValue(value), [value])
  const fileUrl = useMemo(() => buildAttachmentFileUrl(attachment?.storagePath), [attachment?.storagePath])
  const rawValue = value.trim()
  const hasRawValue = rawValue.length > 0
  const rawFileUrl = useMemo(
    () => (attachment ? null : buildAttachmentFileUrl(rawValue)),
    [attachment, rawValue],
  )
  const effectivePermitNumber = permitNumber?.trim() || draftPermitKey

  const handleFileUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      if (!tenantId) {
        setUploadError('กรุณาเลือก Tenant ก่อนอัปโหลดไฟล์')
        event.target.value = ''
        return
      }

      try {
        setUploadError(null)
        setIsUploading(true)

        const formData = new FormData()
        formData.append('tenantId', tenantId)
        formData.append('permitNumber', effectivePermitNumber)
        if (fieldCode?.trim()) {
          formData.append('documentCode', fieldCode.trim())
        }
        formData.append('file', file)

        const response = await uploadPermitAttachment(formData)
        const nextValue: AttachmentValue = {
          fileName: response.fileName || file.name,
          storedFileName: response.storedFileName || undefined,
          storagePath: response.storagePath,
          contentType: response.contentType || file.type || undefined,
          size: response.size ?? file.size,
        }
        onChange(JSON.stringify(nextValue))
      } catch (error) {
        console.error('Failed to upload attachment field file', error)
        setUploadError('อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
      } finally {
        setIsUploading(false)
        event.target.value = ''
      }
    },
    [effectivePermitNumber, fieldCode, onChange, tenantId],
  )

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <input
            type="file"
            onChange={handleFileUpload}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            disabled={disabled || isUploading}
          />
          <Button size="sm" variant="outline" disabled={disabled || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload file
              </>
            )}
          </Button>
        </div>

        {attachment || hasRawValue ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange('')}
            disabled={disabled || isUploading}
            className="text-red-600 hover:text-red-700"
          >
            <X className="mr-1 h-4 w-4" />
            Remove
          </Button>
        ) : null}
      </div>

      {attachment ? (
        <div className="rounded border border-blue-200 bg-blue-50 p-2">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-blue-900">{attachment.fileName}</p>
            {fileUrl ? (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Open
              </a>
            ) : null}
          </div>
        </div>
      ) : hasRawValue ? (
        <div className="rounded border border-blue-200 bg-blue-50 p-2">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-blue-900">{rawValue}</p>
            {rawFileUrl ? (
              <a
                href={rawFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Open
              </a>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-500">No file attached</p>
      )}

      {uploadError ? <p className="text-xs text-red-600">{uploadError}</p> : null}
    </div>
  )
}

interface SearchableSelectDropdownProps {
  fieldId: string
  placeholder: string
  options: Array<{ value: string; label: string }>
  value: string
  isLoading: boolean
  disabled: boolean
  datasourceParams: Record<string, string>
  datasourceParamsKey: string
  selectedValues: string[]
  canSearchRemotely: boolean
  remoteSearchOnly?: boolean
  remoteSearchMinChars?: number
  onChange: (value: string) => void
}

function SearchableSelectDropdown({
  fieldId,
  placeholder,
  options,
  value,
  isLoading,
  disabled,
  datasourceParams,
  datasourceParamsKey,
  selectedValues,
  canSearchRemotely,
  remoteSearchOnly = false,
  remoteSearchMinChars = 1,
  onChange,
}: SearchableSelectDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [remoteOptions, setRemoteOptions] = useState<TemplateFieldDatasourceItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const paramsRef = useRef<Record<string, string>>(datasourceParams)
  const selectedValuesRef = useRef<string[]>(selectedValues)
  const requestIdRef = useRef(0)

  useEffect(() => {
    paramsRef.current = datasourceParams
  }, [datasourceParamsKey, datasourceParams])

  useEffect(() => {
    selectedValuesRef.current = selectedValues
  }, [selectedValues])

  useEffect(() => {
    if (!canSearchRemotely) {
      return
    }
    const term = searchTerm.trim()
    if (remoteSearchOnly && term.length < Math.max(1, remoteSearchMinChars)) {
      setRemoteOptions([])
      setIsSearching(false)
      return
    }
    if (!term) {
      setRemoteOptions([])
      setIsSearching(false)
      return
    }

    const requestId = ++requestIdRef.current
    const timer = window.setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await getFieldDatasourceOptions(
          fieldId,
          paramsRef.current,
          term,
          1,
          10,
          selectedValuesRef.current,
        )
        if (requestIdRef.current !== requestId) {
          return
        }
        setRemoteOptions(response.items ?? [])
      } catch {
        if (requestIdRef.current !== requestId) {
          return
        }
        setRemoteOptions([])
      } finally {
        if (requestIdRef.current === requestId) {
          setIsSearching(false)
        }
      }
    }, 300)

    return () => window.clearTimeout(timer)
  }, [canSearchRemotely, fieldId, remoteSearchMinChars, remoteSearchOnly, searchTerm])

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term && !remoteSearchOnly) {
      return options
    }
    if (!term && remoteSearchOnly) {
      return []
    }
    if (canSearchRemotely) {
      return remoteOptions
    }
    return options.filter((option) => {
      const labelMatch = option.label?.toLowerCase().includes(term) ?? false
      const valueMatch = option.value?.toLowerCase().includes(term) ?? false
      return labelMatch || valueMatch
    })
  }, [options, searchTerm, canSearchRemotely, remoteOptions, remoteSearchOnly])

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSearchTerm('')
    }
  }, [])

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger className="bg-gray-50 border-gray-200">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <div
          className="border-b border-gray-100 p-2"
          onKeyDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search..."
            className="h-8 border-gray-200"
          />
        </div>
        <SelectItem value="_empty">{placeholder}</SelectItem>
        {isSearching ? (
          <SelectItem value={`${fieldId}_searching`} disabled>
            Searching...
          </SelectItem>
        ) : remoteSearchOnly && searchTerm.trim().length < Math.max(1, remoteSearchMinChars) ? (
          <SelectItem value={`${fieldId}_type_more`} disabled>
            Type at least {Math.max(1, remoteSearchMinChars)} characters
          </SelectItem>
        ) : filteredOptions.length === 0 ? (
          <SelectItem value={`${fieldId}_no_results`} disabled>
            No results
          </SelectItem>
        ) : (
          filteredOptions
            .filter((option) => Boolean(option?.value))
            .map((option) => (
              <SelectItem key={`${fieldId}_${option.value}`} value={option.value}>
                {option.label ?? option.value}
              </SelectItem>
            ))
        )}
      </SelectContent>
    </Select>
  )
}

interface MultiSelectDropdownProps {
  fieldId: string
  placeholder: string
  options: Array<{ value: string; label: string }>
  selectedValues: string[]
  isLoading: boolean
  disabled?: boolean
  datasourceParams?: Record<string, string>
  datasourceParamsKey?: string
  canSearchRemotely?: boolean
  remoteSearchOnly?: boolean
  remoteSearchMinChars?: number
  onChange: (values: string[]) => void
}

function MultiSelectDropdown({
  fieldId,
  placeholder,
  options,
  selectedValues,
  isLoading,
  disabled = false,
  datasourceParams = {},
  datasourceParamsKey = "",
  canSearchRemotely = false,
  remoteSearchOnly = false,
  remoteSearchMinChars = 1,
  onChange,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [remoteOptions, setRemoteOptions] = useState<TemplateFieldDatasourceItem[]>([])
  const [selectedLabelMap, setSelectedLabelMap] = useState<Record<string, string>>({})
  const [isSearching, setIsSearching] = useState(false)
  const paramsRef = useRef<Record<string, string>>(datasourceParams)
  const selectedValuesRef = useRef<string[]>(selectedValues)
  const requestIdRef = useRef(0)

  useEffect(() => {
    paramsRef.current = datasourceParams
  }, [datasourceParams, datasourceParamsKey])

  useEffect(() => {
    selectedValuesRef.current = selectedValues
  }, [selectedValues])

  useEffect(() => {
    if (open) {
      return
    }
    requestIdRef.current += 1
    setSearchTerm('')
    setRemoteOptions([])
    setIsSearching(false)
  }, [open])

  useEffect(() => {
    if (!canSearchRemotely) {
      return
    }
    const term = searchTerm.trim()
    if (remoteSearchOnly && term.length < Math.max(1, remoteSearchMinChars)) {
      setRemoteOptions([])
      setIsSearching(false)
      return
    }
    if (!term) {
      setRemoteOptions([])
      setIsSearching(false)
      return
    }

    const requestId = ++requestIdRef.current
    const timer = window.setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await getFieldDatasourceOptions(
          fieldId,
          paramsRef.current,
          term,
          1,
          10,
          selectedValuesRef.current,
        )
        if (requestIdRef.current !== requestId) {
          return
        }
        setRemoteOptions(response.items ?? [])
      } catch {
        if (requestIdRef.current !== requestId) {
          return
        }
        setRemoteOptions([])
      } finally {
        if (requestIdRef.current === requestId) {
          setIsSearching(false)
        }
      }
    }, 300)

    return () => window.clearTimeout(timer)
  }, [canSearchRemotely, fieldId, remoteSearchMinChars, remoteSearchOnly, searchTerm])

  const optionMap = useMemo(() => {
    const map = new Map<string, string>()
    options.forEach((option) => {
      if (option.value) {
        map.set(option.value, option.label ?? option.value)
      }
    })
    remoteOptions.forEach((option) => {
      if (option.value) {
        map.set(option.value, option.label ?? option.value)
      }
    })
    return map
  }, [options, remoteOptions])

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (canSearchRemotely) {
      if (!term && !remoteSearchOnly) {
        return options
      }
      if (!term && remoteSearchOnly) {
        return []
      }
      return remoteOptions.map((item) => ({
        value: item.value,
        label: item.label ?? item.value,
      }))
    }
    if (!term) {
      return options
    }
    return options.filter((option) => {
      const labelMatch = option.label?.toLowerCase().includes(term) ?? false
      const valueMatch = option.value?.toLowerCase().includes(term) ?? false
      return labelMatch || valueMatch
    })
  }, [canSearchRemotely, options, remoteOptions, remoteSearchOnly, searchTerm])

  const selectedDisplayItems = useMemo(() => {
    return selectedValues.map((selectedValue) => {
      const cachedLabel = selectedLabelMap[selectedValue]
      return {
        value: selectedValue,
        label: cachedLabel ?? optionMap.get(selectedValue) ?? selectedValue,
      }
    })
  }, [selectedValues, optionMap, selectedLabelMap])

  const handleToggle = useCallback(
    (optionValue: string, optionLabel?: string) => {
      const next = new Set(selectedValues)
      if (next.has(optionValue)) {
        next.delete(optionValue)
        setSelectedLabelMap((prev) => {
          if (!(optionValue in prev)) {
            return prev
          }
          const cloned = { ...prev }
          delete cloned[optionValue]
          return cloned
        })
      } else {
        next.add(optionValue)
        const resolvedLabel = optionLabel ?? optionMap.get(optionValue) ?? optionValue
        setSelectedLabelMap((prev) => ({
          ...prev,
          [optionValue]: resolvedLabel,
        }))
      }
      onChange(Array.from(next))
    },
    [selectedValues, onChange, optionMap],
  )

  const handleSelectAll = useCallback(() => {
    onChange(options.map((option) => option.value ?? '').filter(Boolean))
  }, [onChange, options])

  const handleClearAll = useCallback(() => {
    onChange([])
  }, [onChange])

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen(true)
          }
        }}
        onKeyDown={(event) => {
          if (disabled) {
            return
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen(true)
          }
        }}
        className={cn(
          "flex w-full flex-wrap items-center gap-2 rounded-md border border-sky-100 bg-white px-3 py-2 text-left text-sm text-sky-700 shadow-sm transition-colors",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-sky-200",
        )}
      >
        {selectedDisplayItems.length === 0 ? (
          <span className="text-sky-700">{placeholder}</span>
        ) : (
          <>
            {selectedDisplayItems.map((item) => (
              <span
                key={`${fieldId}_${item.value}`}
                className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-700 shadow-sm"
              >
                <span>{item.label}</span>
                <button
                  type="button"
                  className="rounded-full text-sky-400 hover:text-sky-700"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleToggle(item.value, item.label)
                  }}
                  aria-label={`Remove ${item.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </>
        )}
      </div>

      {open ? (
        <div className="mt-2 rounded-lg border border-sky-100 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-sky-100 p-3">
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="ค้นหา..."
              className="h-9 border-gray-200"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm text-sky-600 hover:text-sky-700"
            >
              ปิด
            </button>
          </div>
          <div className="flex items-center justify-between border-b border-sky-100 px-3 py-2 text-xs text-sky-600">
            <button type="button" onClick={handleSelectAll} className="hover:text-sky-700">
              เลือกทั้งหมด
            </button>
            <button type="button" onClick={handleClearAll} className="hover:text-sky-700">
              ล้างทั้งหมด
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto p-3 text-sm">
            {(canSearchRemotely ? isSearching : isLoading) ? (
              <p className="text-center text-gray-500">กำลังโหลด...</p>
            ) : remoteSearchOnly && searchTerm.trim().length < Math.max(1, remoteSearchMinChars) ? (
              <p className="text-center text-gray-500">Type at least {Math.max(1, remoteSearchMinChars)} characters</p>
            ) : filteredOptions.length === 0 ? (
              <p className="text-center text-gray-500">ไม่พบข้อมูล</p>
            ) : (
              filteredOptions.map((option) => {
                const optionValue = option.value ?? ''
                const checked = selectedValues.includes(optionValue)
                return (
                  <label
                    key={`${fieldId}_${optionValue}`}
                    className="flex cursor-pointer items-center justify-between rounded-md px-2 py-1 text-sky-700 hover:bg-sky-50"
                  >
                    <span>{option.label ?? option.value}</span>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => handleToggle(optionValue, option.label ?? option.value)}
                      className="border-gray-300"
                    />
                  </label>
                )
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

